import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, RefreshCcw, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { useLang } from '../context/LanguageContext';

const CODE_LEN = 6;
const RESEND_COOLDOWN = 60; // seconds

export default function VerifyCodePage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [email] = useState(() => sessionStorage.getItem('wyse_reset_email') || '');
  const [digits, setDigits] = useState(Array(CODE_LEN).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(RESEND_COOLDOWN);
  const inputsRef = useRef([]);

  // No email in session → bounce back.
  useEffect(() => {
    if (!email) nav('/forgot-password', { replace: true });
  }, [email, nav]);

  // Resend countdown.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const handleDigit = (i, v) => {
    const clean = (v || '').replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[i] = clean;
    setDigits(next);
    if (clean && i < CODE_LEN - 1) inputsRef.current[i + 1]?.focus();
  };
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) inputsRef.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, CODE_LEN);
    if (!txt) return;
    e.preventDefault();
    const next = Array(CODE_LEN).fill('');
    for (let i = 0; i < txt.length; i += 1) next[i] = txt[i];
    setDigits(next);
    inputsRef.current[Math.min(txt.length, CODE_LEN - 1)]?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const code = digits.join('');
    if (code.length !== CODE_LEN) { toast.error(t('vc.enterCode')); return; }
    setSubmitting(true);
    try {
      const out = await api.passwordVerifyCode(email, code);
      sessionStorage.setItem('wyse_reset_session', out.reset_session);
      sessionStorage.setItem('wyse_reset_email', email);
      nav('/reset-password');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('vc.verifyFailed'));
      setDigits(Array(CODE_LEN).fill(''));
      inputsRef.current[0]?.focus();
    } finally { setSubmitting(false); }
  };

  const resend = async () => {
    if (resendIn > 0) return;
    try {
      await api.passwordRequestCode(email, '');
      toast.success(t('vc.resendOk'));
      setResendIn(RESEND_COOLDOWN);
      setDigits(Array(CODE_LEN).fill(''));
      inputsRef.current[0]?.focus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('vc.resendErr'));
    }
  };

  return (
    <>
      <Header />
      <section className="min-h-[70vh] flex items-center justify-center px-6 py-14">
        <div data-testid="verify-code-card"
             className="w-full max-w-md bg-white rounded-2xl border border-neutral-100 shadow-[0_25px_60px_-30px_rgba(20,17,15,0.25)] p-8 md:p-10">
          <Link to="/forgot-password" className="inline-flex items-center gap-1.5 text-[12.5px] text-neutral-500 hover:text-[#14110f] mb-6">
            <ArrowLeft className="w-3.5 h-3.5" />{t('vc.back')}
          </Link>
          <p className="section-eyebrow mb-2">{t('vc.eyebrow')}</p>
          <h1 className="text-[26px] font-extrabold text-[#14110f] leading-tight flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#c89200]" />{t('vc.title')}
          </h1>
          <p className="mt-2 text-[13.5px] text-neutral-500 break-all">
            {t('vc.desc1')} <strong>{email}</strong>. {t('vc.desc2')}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div data-testid="code-inputs" className="grid grid-cols-6 gap-2" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input key={i} data-testid={`code-digit-${i}`}
                       ref={(el) => { inputsRef.current[i] = el; }}
                       autoFocus={i === 0}
                       inputMode="numeric" maxLength={1} pattern="\d"
                       value={d}
                       onChange={(e) => handleDigit(i, e.target.value)}
                       onKeyDown={(e) => handleKey(i, e)}
                       className="text-center text-[22px] font-bold py-3 rounded-md border border-neutral-200 focus:border-[#f5b300] focus:ring-2 focus:ring-[#f5b300]/20 outline-none text-[#14110f] bg-white" />
              ))}
            </div>

            <button type="submit" data-testid="verify-submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#f5b300] hover:bg-[#e0a200] disabled:opacity-60 text-[#14110f] font-semibold px-7 py-3.5 rounded-md shadow-[0_14px_30px_-10px_rgba(245,179,0,0.7)] transition-all">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              {t('vc.verify')}
            </button>

            <button type="button" data-testid="resend-code"
                    onClick={resend} disabled={resendIn > 0}
                    className="w-full inline-flex items-center justify-center gap-2 text-[12.5px] text-[#c89200] font-semibold hover:underline disabled:text-neutral-400 disabled:no-underline">
              <RefreshCcw className="w-3.5 h-3.5" />
              {resendIn > 0 ? `${t('vc.resendIn')} ${resendIn}s` : t('vc.resend')}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </>
  );
}
